import { keyBy, mapValues } from 'lodash';
import { observer } from 'mobx-react';
import qs from 'qs';
import React, { useCallback, useMemo } from 'react';

import useStores from 'hooks/use-stores';
import StatisticWidget from 'pages/Search/components/Database/components/StatisticWidget';
import { WidgetCVEProgression, WidgetTweets } from 'pages/stats/total/Widgets';

import useStyles from './styles';

const Widgets = () => {
    const { classes } = useStyles();
    const { statsStore, sTemplatesStore } = useStores();
    const { statsLoading, stats, types } = statsStore;
    const {
        bulletins,
        collections,
        avgExploitPublicationDate,
        avgWildExploitPublished,
        exploits,
        avgCvssScore,
        avgCvePerDay,
        avgBulletinsPerDay
    } = stats;

    const { loading: templatesLoading, templates } = sTemplatesStore;
    const loading = statsLoading || templatesLoading;

    const queryToAliasUrlMap = useMemo(() => {
        const dictionary = keyBy(templates, ({ query }) => query);
        return mapValues(dictionary, ({ aliasUrl }) => aliasUrl);
    }, [templates]);

    const buildHref = useCallback(
        (query: string) => {
            return queryToAliasUrlMap[query] || '/search' + qs.stringify({ query }, { addQueryPrefix: true });
        },
        [queryToAliasUrlMap]
    );

    return (
        <div className={classes.container}>
            <WidgetCVEProgression className={classes.progression} />
            <WidgetTweets hideFullList className={classes.social} />
            <StatisticWidget
                loading={loading}
                value={bulletins > 999999 ? (bulletins / 1000000).toFixed() + 'mln +' : bulletins}
                description={'Security advisories and articles'}
                href={buildHref('type:*')}
                className={classes.counter}
            />
            <StatisticWidget
                loading={loading}
                value={Number(collections) || Object.keys(types || {}).length || 0}
                description={'Software vendors and other security sources'}
                href={buildHref('type:cve order:published')}
                className={classes.counter}
            />
            <StatisticWidget
                loading={loading}
                value={(avgExploitPublicationDate / (60 * 60)).toFixed() + 'h'}
                description={'Average hours to exploit published'}
                href={buildHref('bulletinFamily:exploit last 7 days')}
                className={classes.counter}
            />
            <StatisticWidget
                loading={loading}
                value={(avgWildExploitPublished / (60 * 60 * 24)).toFixed() + 'd'}
                description={'Average days to cve exploited in the wild'}
                href={buildHref('enchantments.exploitation.wildExploited:true')}
                className={classes.counter}
            />
            <StatisticWidget
                loading={loading}
                value={(exploits / 1000).toFixed() + 'k +'}
                description={'Exploits for popular software and systems'}
                href={buildHref('bulletinFamily:exploit order:published')}
                className={classes.counter}
            />
            <StatisticWidget
                loading={loading}
                value={(avgCvssScore || 0).toFixed(2)}
                description={'Average CVSS score from beginning of time'}
                href={buildHref('cvss.score:10')}
                className={classes.counter}
            />
            <StatisticWidget
                loading={loading}
                value={(avgCvePerDay || 0).toFixed(0)}
                description={'Average CVE released per day'}
                href={buildHref('type:cve last 1 days')}
                className={classes.counter}
            />
            <StatisticWidget
                loading={loading}
                value={(avgBulletinsPerDay || 0).toFixed(0)}
                description={'Average Entries added per day'}
                href={buildHref('order:published last 1 days')}
                className={classes.counter}
            />
        </div>
    );
};

export default observer(Widgets);
